import React, { useEffect, useRef, useState } from 'react';
import { Gift, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { sendChatTip } from '../../api/messages';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import useAppConfig from '../../hooks/useAppConfig';
import InsufficientBalanceDialog from '../InsufficientBalanceDialog';
import { fmtCredits, creditsToEur, CREATOR_PRICE_OPTIONS } from '../../lib/currency';

const newRef = () =>
  (window.crypto?.randomUUID?.() || `ref_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);

/**
 * ChatTipDialog — Phase F4. 🎁 Tip inside the chat, Voce Pro recipients
 * ONLY. Reuses the EXISTING Tip/Credits system (same admin presets, wallet
 * balance, ledger, fee). clientRef makes retries idempotent (no double tip).
 */
const ChatTipDialog = ({ open, onOpenChange, username, onSent, t }) => {
  const { toast } = useToast();
  const { user, refresh } = useAuth();
  const { config } = useAppConfig();
  const presets = (Array.isArray(config.tip_presets) && config.tip_presets.length > 0)
    ? config.tip_presets
    : CREATOR_PRICE_OPTIONS;
  const [amount, setAmount] = useState(null);
  const [sending, setSending] = useState(false);
  const [insufficientOpen, setInsufficientOpen] = useState(false);
  const clientRefRef = useRef(newRef());

  useEffect(() => {
    if (open) {
      clientRefRef.current = newRef();
      setAmount(presets[1] || presets[0] || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const send = async () => {
    if (!amount || sending) return;
    setSending(true);
    try {
      const res = await sendChatTip(username, amount, clientRefRef.current);
      toast({ title: t('tip.success'), description: `${fmtCredits(amount)} → @${username}` });
      refresh?.();
      onOpenChange(false);
      onSent?.(res.message || null);
    } catch (err) {
      if (err.response?.status === 402) {
        setInsufficientOpen(true);
      } else {
        toast({
          title: t('tip.failed'),
          description: String(err.response?.data?.detail || '').split('|').pop() || '',
          variant: 'destructive',
        });
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-sm" data-testid="chat-tip-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="h-4 w-4 gold-text" /> {t('tip.title', { username })}
            </DialogTitle>
            <DialogDescription>{t('tip.desc')}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-2" data-testid="chat-tip-presets">
            {presets.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setAmount(p)}
                className={`rounded-lg border px-2 py-2 text-center ${
                  amount === p
                    ? 'border-amber-500 bg-amber-500/10 gold-text'
                    : 'border-border bg-card text-foreground hover:bg-secondary'
                }`}
                data-testid={`chat-tip-preset-${p}`}
              >
                <span className="block text-sm font-bold">{fmtCredits(p)}</span>
                <span className="block text-[10px] text-muted-foreground">€{creditsToEur(p).toFixed(2)}</span>
              </button>
            ))}
          </div>
          <Button
            onClick={send}
            disabled={sending || !amount}
            className="gold-gradient w-full text-black"
            data-testid="chat-tip-send-btn"
          >
            {sending
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : t('tip.send', { amount: amount ? fmtCredits(amount) : '' })}
          </Button>
        </DialogContent>
      </Dialog>
      <InsufficientBalanceDialog
        open={insufficientOpen}
        onOpenChange={setInsufficientOpen}
        balance={user?.wallet_balance || 0}
        required={amount || 0}
        context="tip"
      />
    </>
  );
};

export default ChatTipDialog;
